// Slip Autopsy page (canonical, post-SA-9 cleanup).
//
// History: shipped as SlipAutopsyPage.v2.tsx during the SA-4 → SA-8
// ramp behind the SLIP_AUTOPSY_V2 localStorage flag. SA-9 cleanup
// renamed this file to SlipAutopsyPage.tsx, deleted the legacy
// SlipAutopsyPageLegacy.tsx + the feature-flag shim + the
// featureFlags.ts module, and made this the canonical export.
//
// Per SlipAutopsy_Redesign_Brief_v1.0 §02.1, the page has three
// sections in brief order (N02):
//   Section A — Betting Intelligence Profile  (top, always visible)
//   Section B — Run an Autopsy                (middle, the tool)
//   Section C — Autopsy History                (bottom, always visible)
//
// Sidebar collapse state persists to localStorage under
// `strikza:slip-autopsy:sidebar-collapsed`.

import { useEffect, useRef, useState } from "react";

import { useAuthSession } from "../../auth/useAuthSession.ts";
import { useUserTier } from "../../auth/useUserTier.ts";
import { Icon } from "../../components/icons/Icon.tsx";
import { AutopsyForm } from "../../components/slip-autopsy-2/AutopsyForm.tsx";
import { AutopsyHistory } from "../../components/slip-autopsy-2/AutopsyHistory.tsx";
import { BookmakerPerformance } from "../../components/slip-autopsy-2/BookmakerPerformance.tsx";
import { MarketPerformanceMatrix } from "../../components/slip-autopsy-2/MarketPerformanceMatrix.tsx";
import { OddsRangeAnalysis } from "../../components/slip-autopsy-2/OddsRangeAnalysis.tsx";
import { PatternAlerts } from "../../components/slip-autopsy-2/PatternAlerts.tsx";
import { ProfileHeader } from "../../components/slip-autopsy-2/ProfileHeader.tsx";
import { SlipAutopsySidebar } from "../../components/slip-autopsy-2/SlipAutopsySidebar.tsx";
import { useBettingProfile } from "../../slips/useBettingProfile.ts";

const COLLAPSED_KEY = "strikza:slip-autopsy:sidebar-collapsed";

function readCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}
function writeCollapsed(v: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(COLLAPSED_KEY, v ? "1" : "0");
  } catch {
    /* ignore */
  }
}

export function SlipAutopsyPage(): JSX.Element {
  const { user } = useAuthSession();
  const { isPro } = useUserTier();
  // SA-4/4 — read the SA-2 user_betting_profile row. Empty defaults
  // are the happy path per N04; every consumer renders its own
  // meaningful empty state.
  const { profile } = useBettingProfile();

  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() =>
    readCollapsed(),
  );
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Section refs for the sidebar's "Profile / Run autopsy / History"
  // nav rows to scroll into view.
  const sectionARef = useRef<HTMLElement>(null);
  const sectionBRef = useRef<HTMLElement>(null);
  const sectionCRef = useRef<HTMLElement>(null);

  const scrollTo = (
    ref: React.RefObject<HTMLElement | null>,
  ): void => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const toggleSidebar = (): void => {
    setSidebarCollapsed((v) => {
      const next = !v;
      writeCollapsed(next);
      return next;
    });
  };

  // First-paint: if the user landed via a #section anchor, honour it.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash.replace("#", "");
    if (hash === "tool") sectionBRef.current?.scrollIntoView({ behavior: "auto" });
    else if (hash === "history") sectionCRef.current?.scrollIntoView({ behavior: "auto" });
  }, []);

  return (
    <div className="sa-root" data-page="slip-autopsy-v2">
      <div className="slip-autopsy-2">
        <SlipAutopsySidebar
          activeView="profile"
          userEmail={user?.email ?? null}
          pro={isPro}
          collapsed={sidebarCollapsed}
          onToggleCollapse={toggleSidebar}
          mobileOpen={mobileSidebarOpen}
          onCloseMobile={() => setMobileSidebarOpen(false)}
          onNewAutopsy={() => scrollTo(sectionBRef)}
          onSelectProfile={() => scrollTo(sectionARef)}
          onSelectTool={() => scrollTo(sectionBRef)}
          onSelectHistory={() => scrollTo(sectionCRef)}
        />

        <div className="sa-autopsy-main" data-testid="sa-autopsy-main">
          <header className="sa-autopsy-topbar" data-testid="sa-autopsy-topbar">
            <button
              type="button"
              className="sa-autopsy-topbar-btn sa-mobile-only-topbar"
              onClick={() => setMobileSidebarOpen(true)}
              aria-label="Open autopsy navigation"
              data-testid="sa-autopsy-topbar-mobile-menu"
              style={{
                background: "transparent",
                border: "1px solid var(--sa-hairline)",
                borderRadius: 3,
                color: "var(--sa-fg-secondary)",
                padding: "6px 10px",
                fontFamily: "var(--sa-font-mono)",
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              <Icon name="menu" size={14} />
            </button>

            <nav
              className="sa-autopsy-breadcrumb"
              aria-label="Slip Autopsy breadcrumb"
              data-testid="sa-autopsy-breadcrumb"
            >
              <span>SLIP AUTOPSY</span>
              <span className="sa-crumb-sep" aria-hidden>
                /
              </span>
              <span style={{ color: "var(--sa-fg-primary)" }}>
                Your Profile
              </span>
            </nav>

            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={() => scrollTo(sectionBRef)}
                data-testid="sa-autopsy-topbar-new"
                style={{
                  fontFamily: "var(--sa-font-mono)",
                  fontSize: 11,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  padding: "6px 12px",
                  background: "var(--sa-accent-saffron)",
                  color: "var(--sa-bg-base)",
                  border: "none",
                  borderRadius: 3,
                  cursor: "pointer",
                }}
              >
                <Icon name="plus" size={11} /> &nbsp;Run autopsy
              </button>
            </div>
          </header>

          <div className="sa-autopsy-scroll">
            <div className="sa-autopsy-col">
              {/* ─── Section A — Profile (always visible, FIRST per N02) ─── */}
              <section
                className="sa-autopsy-section"
                data-section="a"
                data-testid="sa-section-a"
                ref={sectionARef}
              >
                <div className="sa-section-eyebrow">Intelligence Profile</div>

                {/* A1 · Profile header — SA-4/4 */}
                <ProfileHeader profile={profile} pro={isPro} />

                {/* A2 · Market performance matrix — SA-4/4 */}
                <div data-testid="sa-section-a-a2">
                  <div
                    className="sa-section-eyebrow"
                    style={{ marginTop: 8 }}
                  >
                    A2 · Market performance
                  </div>
                  <MarketPerformanceMatrix
                    data={profile.market_performance}
                    pro={isPro}
                  />
                </div>

                {/* A3 · Odds range analysis — SA-4/5 */}
                <div data-testid="sa-section-a-a3">
                  <div className="sa-section-eyebrow" style={{ marginTop: 8 }}>
                    A3 · Odds range analysis
                  </div>
                  <OddsRangeAnalysis data={profile.odds_range_performance} />
                </div>

                {/* A4 · Bookmaker performance — SA-4/5 */}
                <div data-testid="sa-section-a-a4">
                  <div className="sa-section-eyebrow" style={{ marginTop: 8 }}>
                    A4 · Bookmaker performance
                  </div>
                  <BookmakerPerformance
                    data={profile.bookmaker_performance}
                    pro={isPro}
                  />
                </div>

                {/* A5 · Pattern alerts — SA-4/5 */}
                <div data-testid="sa-section-a-a5">
                  <div className="sa-section-eyebrow" style={{ marginTop: 8 }}>
                    A5 · Pattern alerts
                  </div>
                  <PatternAlerts
                    alerts={profile.pattern_alerts}
                    totalSlips={profile.total_slips}
                    pro={isPro}
                  />
                </div>
              </section>

              {/* ─── Section B — Run an Autopsy (SA-5 fills) ─── */}
              <section
                className="sa-autopsy-section"
                data-section="b"
                data-testid="sa-section-b"
                ref={sectionBRef}
              >
                <div className="sa-section-eyebrow">Tool</div>
                <h3
                  className="sa-section-title"
                  style={{ fontSize: 18 }}
                >
                  Run an autopsy
                </h3>
                <AutopsyForm />
              </section>

              {/* ─── Section C — History (SA-8 fills) ─── */}
              <section
                className="sa-autopsy-section"
                data-section="c"
                data-testid="sa-section-c"
                ref={sectionCRef}
              >
                <div className="sa-section-eyebrow">Record</div>
                <h3
                  className="sa-section-title"
                  style={{ fontSize: 18 }}
                >
                  Autopsy history
                </h3>
                <AutopsyHistory pro={isPro} />
              </section>
            </div>
          </div>
        </div>
      </div>

      {/* The .sa-mobile-only-topbar utility is local to this page —
          AppShell already hides on mobile via the global hamburger.
          Below 640px we still want a hamburger to open the secondary
          sidebar drawer. Inline minimal style; could move to SA-3
          in cleanup. */}
      <style>{`
        .sa-mobile-only-topbar { display: none; }
        @media (max-width: 640px) {
          .sa-mobile-only-topbar { display: inline-flex; }
        }
      `}</style>
    </div>
  );
}

// ─── Section A/B/C all live now ─────────────────────────────────
// SA-4/5 retired the A placeholder; SA-5 retired the B placeholder;
// SA-8 retired the C placeholder by mounting <AutopsyHistory />.
// Only the data-source layer remains (SA-6 fills it).
